import styles from './index.module.less';
import { App } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { HairstyleBloggerLabelModel } from '@/types/hairstyle';
import { HairstyleLabelsAlert } from '@/components/Common/HairstyleLabelsAlert';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';
import { InfluencersLabelsAlert } from '@/components/Common/InfluencersLabelsAlert';
import { useUserProvider } from '@/context/UserProvider';
import { lowerCase } from 'lodash';
import Link from 'next/link';
import LabelFilterArea from '@/components/Common/LabelFilterArea';

const InfluencersTopContent = ({
  hairstyleCount,
  bloggerCount,
  hairstyleLabels,
  bloggerLabels,
  searchEmpty = false,
  type = 'hairstyle',
  onChangeType
}: {
  hairstyleCount?: number;
  bloggerCount?: number;
  hairstyleLabels: HairstyleBloggerLabelModel[];
  bloggerLabels: string[];
  searchEmpty?: boolean;
  type: 'hairstyle' | 'influencers';
  onChangeType?: (type: 'hairstyle' | 'influencers', item?: string, value?: string) => void;
  onBloggerClick?: () => void;
}) => {
  const router = useRouter();
  const { label = '' } = router.query;
  const { message } = App.useApp();

  const { isLogin, onOpenLoginModal } = useUserProvider();

  const [currentVal, setCurrentVal] = useState('');
  const [currentType, setCurrentType] = useState(type);
  const [selectItem, setSelectItem] = useState<string>(`${label ?? ''}`);
  const [selectImgItem, setSelectImgItem] = useState<string>(`${label ?? ''}`);
  const [openHairstyleList, setOpenHairstyleList] = useState<HairstyleBloggerLabelModel[]>([]);
  const [openInfluencerList, setOpenInfluencerList] = useState<string[]>([]);

  const { width } = useScreenSize();

  const onSeachClick = () => {
    if (!(currentVal?.length > 0)) {
      message.error('Please enter search content.');
      return;
    }
    if (onChangeType) {
      setSelectImgItem(`${label ?? 'ALL'}`);
      onChangeType('influencers', null, currentVal);
    }
  };

  const onChangeTypeClick = (index: 'hairstyle' | 'influencers', labelVal?: any) => {
    let tempLabelVal;
    if (index === 'hairstyle') {
      tempLabelVal = labelVal ?? (hairstyleLabels?.length > 0 ? hairstyleLabels[0]?.label : null);
      setSelectItem(tempLabelVal);
    } else {
      tempLabelVal = labelVal ?? (bloggerLabels?.length > 0 ? bloggerLabels[0] : null);

      setSelectImgItem(tempLabelVal);
    }

    if (onChangeType) {
      onChangeType(index, tempLabelVal);
    }

    setCurrentType(index);
  };

  useEffect(() => {
    onChangeTypeClick(type, label);
  }, [type, label]);

  useEffect(() => {
    if (currentVal === null || currentVal === undefined || currentVal === '') {
      const tempLabel =
        `${label ?? ''}`.length > 0 ? `${label ?? ''}` : bloggerLabels?.length > 0 ? bloggerLabels[0] : null;
      setSelectImgItem(tempLabel);
      onChangeType('influencers', tempLabel, null);
      return;
    }
  }, [currentVal]);

  const getTopBtns = () => {
    return [
      {
        text: 'HairStyles',
        path: '/hairstyle'
      },
      {
        text: 'Influencers',
        path: '/influencers'
      }
    ];
  };
  return (
    <div className={styles.content}>
      <div className={styles.topBtns}>
        {getTopBtns().map((item, index) => {
          const styleStr = index === 0 ? `${styles.activeHairstyle}` : `${styles.activeInfluencers}`;
          return (
            <span
              key={item.text}
              className={lowerCase(item.path.substring(1)) === currentType ? styleStr : ''}
              onClick={() => {
                router.push(item.path);
              }}
            >
              {item.text}
            </span>
          );
        })}
      </div>

      {currentType === 'influencers' && (
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search"
            value={currentVal}
            onChange={(e) => setCurrentVal(e?.target?.value)}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') {
                onSeachClick();
              }
            }}
          />
          <span className={styles.searchIcon} onClick={onSeachClick}>
            <SearchOutlined />
          </span>
        </div>
      )}

      <div className={styles.followContainer}>
        {currentType === 'hairstyle' ? (
          <>
            <span style={{ color: 'var(--app-primary-color)', fontSize: '20px' }}>{`${hairstyleCount} `}</span>
            <span>{`Hairstyles Available to Try On. No salon visits needed, no actual haircuts. All in just seconds.`}</span>
          </>
        ) : (
          <>
            {currentType === 'influencers' && !searchEmpty && (
              <>
                <span>{`Follow`}</span>
                <span style={{ color: 'var(--app-primary-color)', fontSize: '20px' }}>{` ${bloggerCount} `}</span>
                <span>{`Top Hairstyle Influencers. Receive weekly curated hairstyle collections and try on their latest styles!`}</span>
              </>
            )}
          </>
        )}
      </div>

      {currentType === 'hairstyle' && (
        <div className={styles.labelsContainer} style={{ display: 'block' }}>
          <LabelFilterArea
            type="image"
            labels={hairstyleLabels}
            selectedItem={selectItem}
            showMoreButton={hairstyleLabels?.length > 30}
            maxDisplayCount={width < 786 ? hairstyleLabels?.length : 30}
            // style={{ marginTop: 50 }}
            layoutMode={width < 786 ? 'scroll' : 'wrap'}
            stickyOnMobile
            onItemClick={(item) => {
              setSelectItem(item);
              onChangeType(currentType, item);
            }}
            onMoreClick={() => setOpenHairstyleList([...hairstyleLabels])}
            onLoginRequired={() => onOpenLoginModal(true)}
            isLogin={isLogin}
          />
        </div>
      )}

      {currentType === 'influencers' && bloggerLabels?.length > 0 && !searchEmpty && (
        <div className={styles.labelsContainer}>
          <LabelFilterArea
            type="text"
            labels={bloggerLabels}
            selectedItem={selectImgItem}
            showMoreButton={bloggerLabels?.length > 30}
            maxDisplayCount={width < 786 ? bloggerLabels?.length : 30}
            layoutMode={width < 786 ? 'scroll' : 'wrap'}
            stickyOnMobile
            onItemClick={(item) => {
              setSelectImgItem(item);
              onChangeType(currentType, item);
            }}
            onMoreClick={() => setOpenInfluencerList([...bloggerLabels])}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 8
            }}
          >
            {/* 红人入驻按钮 */}
            <Link href="/creator-join" target="_blank">
              <div className={styles.submitContainer}>
                <PlusCircleOutlined style={{ marginRight: 4 }} />
                <span>{'Join as Creator'}</span>
              </div>
            </Link>
            {/* 提交你喜欢的红人 */}
            {/* <div>{getSubmitItem()}</div> */}
          </div>
        </div>
      )}

      <HairstyleLabelsAlert
        openListData={openHairstyleList}
        selectItem={selectItem}
        onCloseClick={() => {
          setOpenHairstyleList([]);
        }}
        onSelectClick={(value) => {
          setOpenHairstyleList([]);
          setSelectItem(value);
          onChangeType(currentType, value);
        }}
      />

      <InfluencersLabelsAlert
        openListData={openInfluencerList}
        selectItem={selectImgItem}
        onCloseClick={() => {
          setOpenInfluencerList([]);
        }}
        onSelectClick={(value) => {
          setCurrentVal('');
          setOpenInfluencerList([]);
          setSelectImgItem(value);
          onChangeType(currentType, value);
        }}
      />
    </div>
  );
};

export default InfluencersTopContent;
